/**
 * Cross-account leak guard.
 *
 * greenbook is a static SPA: the page never reloads across a sign-out/sign-in, so the
 * QueryClient, the trading-mode store and the order-settings store all OUTLIVE the credential
 * that filled them. Without this guard, user B signing in on user A's tab renders A's cached
 * `["account","balances"]` (30s staleTime) while the refetch is still in flight — A's real
 * venue balances, shown to B, until something forces a refresh.
 *
 * So: any change of {@link authIdentity} wipes every scrap of per-user state. The stream layer
 * already did this for the socket (see ws.ts `onAuthChange`, "never stream another user's
 * data"); this is the same rule for the cache and the armed-state stores.
 *
 * A silent key refresh keeps the same identity (`sdk:<installId>`), so it does NOT wipe —
 * only an actual credential change does: sign-in, sign-out, or a switch between accounts.
 */
import { authIdentity, subscribeAuth } from "./omauth.js";
import { queryClient } from "./queryClient.js";
import { setTradingMode } from "./trading.js";
import { setOrderSetting } from "./settings.js";
import { resetOrderClient } from "./orderClient.js";

/**
 * Identity that the current in-memory state belongs to. Seeded from the credential already in
 * storage so an ordinary page load (same user, same session) is NOT treated as a switch —
 * otherwise every refresh would reset the user's own trading mode.
 */
let ownerIdentity: string | null = null;

/** Drop everything the previous credential put in memory. */
function wipe(): void {
  // cancel() first: an in-flight refetch started under the OLD credential must not land in the
  // fresh cache. apiGet carries a second, narrower guard for responses that slip past this.
  void queryClient.cancelQueries();
  queryClient.clear();
  // Armed state is per-user too: never inherit someone else's "real money" switch or their
  // unlocked limit orders. Both fall back to their safe defaults.
  setTradingMode("paper");
  setOrderSetting("enable_limit_orders", false);
  // The order SDK's own caches (constraints, open sheets, tracked orders) are per-user too:
  // "Session switch: drop every cache, close the stream". The next ticket builds a fresh client.
  resetOrderClient();
}

/**
 * Install the guard. Call once, before the app renders. Returns the unsubscribe (tests).
 */
export function installSessionGuard(): () => void {
  ownerIdentity = authIdentity();
  return subscribeAuth(() => {
    const next = authIdentity();
    if (next === ownerIdentity) return; // same credential (incl. a silent key refresh)
    ownerIdentity = next;
    wipe();
  });
}
